// Слой 3 контракта печати (T-030): байты на BT Classic SPP принтер с телефона.
//
// Нативная сторона (`BtClassicSppPlugin.java`) нарочно тонкая: connect/write/
// disconnect и ничего сверх — поиск устройства, разрешения при настройке, выбор
// адреса уже сделаны раньше (T-031, привязка принтера). Политика повторов здесь
// общая с COM-портом (T-033, `retryPolicy.ts`) — тот же урок B-023 относится
// к обоим транспортам одинаково.
//
// Разрешение на Bluetooth проверяется ДО цикла повторов, а не внутри него:
// отозванное разрешение не станет другим через 0.5 секунды, и три попытки
// с backoff — это три с лишним секунды ожидания того, что заведомо не изменится.

import { registerPlugin, Capacitor } from '@capacitor/core';

import { PrinterError, PRINTER_TRANSPORTS, type PrinterTransport } from '../printerAdapter';
import { decodePrinterStatus, type PrinterStatus } from '../status';
import { exclusive } from './exclusive';
import {
  CONNECT_TIMEOUT_MS,
  LABEL_TIMEOUT_MS,
  sendWithRetry,
  TimeoutError,
  withTimeout,
} from './retryPolicy';
import { connectAwake, type ConnectFailureKind } from './wakeUp';

// Телефон не отличает «с принтера печатает другое устройство» от «принтер выключен или
// далеко»: в обоих случаях принтер просто не отвечает на вызов. Прежний текст называл
// одну причину уверенно и ошибался — 2026-09-26 его показали на телефоне, который
// сам же и держал принтер зависшим подключением. Текст называет все три.
const PRINTER_BUSY_MESSAGE =
  'Принтер не отвечает: с него печатает другое устройство, он выключен или далеко. Подождите и повторите.';

/**
 * Сколько экран настройки ждёт кнопку «Подключиться». Больше потолка всей лестницы
 * в плагине (`LADDER_DEADLINE_MS`, 10 с): ответ должен прийти от плагина с настоящей
 * причиной, а не оборваться этим таймаутом.
 */
export const MANUAL_CONNECT_TIMEOUT_MS = 12_000;

// Очередь операций и её потолок — общие с Bluetooth оболочки Windows (`exclusive.ts`).
export { QUEUE_WAIT_CAP_MS } from './exclusive';

/** Плагин сам определил, что принтер не ответил на вызов (код отказа из `BtClassicSppPlugin.java`). */
function isUnreachable(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    (error as { code?: unknown }).code === 'UNREACHABLE'
  );
}

/**
 * Чем кончилась попытка подключения (`wakeUp.ts`, коды `BtClassicSppPlugin.java`):
 * - ждать нечего — нет адреса, нет разрешения, Bluetooth выключен или его нет;
 * - молчание — таймаут, `UNREACHABLE` (принтер не ответил на вызов вовсе), `CANCELLED`
 *   (попытку погасили мы сами): принтер спит, занят другим устройством или далеко;
 * - отказ — остальное (`CONNECT_FAILED`): принтер ответил, но не пустил. Раз бывает
 *   и при пробуждении, три раза подряд — нет: например, принтеру нужно сопряжение,
 *   и экран настройки его предложит.
 */
function classifyConnectFailure(error: unknown): ConnectFailureKind {
  const message = error instanceof Error ? error.message : String(error);
  if (
    /Не указан адрес|Нет разрешения|Bluetooth выключен|недоступен на этом устройстве/i.test(message)
  ) {
    return 'permanent';
  }
  if (error instanceof TimeoutError || isUnreachable(error) || hasCode(error, 'CANCELLED')) {
    return 'silent';
  }
  return 'refused';
}

/**
 * Подключиться, дождавшись, пока принтер проснётся или освободится. Каждой попытке —
 * вся лестница плагина (её потолок 10 с): ответ с причиной приходит от плагина.
 */
function connectAwakeVia(
  plugin: BtClassicSppPlugin,
  address: string,
): Promise<{ via?: string } | void> {
  return connectAwake({
    tryConnect: () => withTimeout(plugin.connect({ address }), MANUAL_CONNECT_TIMEOUT_MS),
    afterFailure: () => plugin.disconnect(),
    classify: classifyConnectFailure,
  });
}

/**
 * Отказ подключения — для повара: не дождались принтера значит «не отвечает»;
 * постоянную причину (Bluetooth выключен) называем словами плагина.
 */
function connectFailure(error: unknown, refusedText: string): PrinterError {
  const kind = classifyConnectFailure(error);
  if (kind === 'silent') {
    return new PrinterError(PRINTER_BUSY_MESSAGE, error, 'busy');
  }
  if (kind === 'permanent') {
    const reason = error instanceof Error ? error.message : String(error);
    return new PrinterError(`${refusedText}: ${reason}`, error);
  }
  return new PrinterError(refusedText, error);
}

export type BtPermissionState = 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale';

/** Устройство, найденное поиском рядом (спаренное или нет). */
export type BtFoundDevice = {
  readonly name: string | null;
  readonly address: string;
  readonly bonded: boolean;
  /** Класс устройства «изображение» — принтеры (у V3BT класс 0x0680). */
  readonly printer: boolean;
  readonly majorClass: number;
};

export type BtPairedDevice = {
  readonly name: string;
  readonly address: string;
};

/** Нативный плагин `BtClassicSppPlugin.java`. Регистрируется один раз на модуль. */
export interface BtClassicSppPlugin {
  /**
   * Подключиться к спаренному принтеру.
   *
   * `via` — название сработавшей ступени лестницы подключения (см. плагин). Нужно
   * для диагностики: когда у клиента откажет, мы увидим, докуда дошла лестница,
   * вместо общего «нет связи». Необязательное — старая оболочка его не отдаёт,
   * а веб-слой едет по воздуху и обязан пережить это молча.
   */
  connect(options: { address: string }): Promise<{ via?: string } | void>;
  write(options: { data: number[] }): Promise<void>;
  disconnect(): Promise<void>;
  /** `scan` — разрешение на поиск рядом; старая оболочка его не отдаёт. */
  checkPermissions(): Promise<{ bluetooth: BtPermissionState; scan?: BtPermissionState }>;
  requestPermissions(): Promise<{ bluetooth: BtPermissionState }>;
  /** Разрешение на поиск принтера рядом (`BLUETOOTH_SCAN`, без геолокации). Нет в старой оболочке. */
  requestScanPermission(): Promise<{ bluetooth: BtPermissionState; scan?: BtPermissionState }>;
  /** Поиск рядом (~12 с). По ходу шлёт `deviceFound`. Нет в старой оболочке. */
  discoverDevices(): Promise<{ devices: BtFoundDevice[] }>;
  stopDiscovery(): Promise<void>;
  /** Сопряжение из приложения; PIN принтера плагин вводит сам. Нет в старой оболочке. */
  pair(options: { address: string; pin?: string }): Promise<{ bonded: boolean }>;
  /**
   * Уже спаренные устройства (T-031) — не живой поиск, см. плагин.
   *
   * `enabled` — включён ли адаптер. Необязательное: старая оболочка его не отдаёт,
   * тогда считаем «включён» (см. `isBluetoothOff`).
   */
  listPairedDevices(): Promise<{ devices: BtPairedDevice[]; enabled?: boolean }>;
  /** Системный экран Bluetooth (сопряжение остаётся за системой). Нет в старой оболочке. */
  openBluetoothSettings(): Promise<void>;
  /** Системный диалог «включить Bluetooth?». Нет в старой оболочке. */
  requestEnable(): Promise<{ enabled: boolean }>;
  /** Событие `resume` — человек вернулся в приложение (например, из системных настроек). */
  addListener(eventName: 'resume', listener: () => void): Promise<{ remove: () => Promise<void> }>;
  addListener(
    eventName: 'deviceFound',
    listener: (device: BtFoundDevice) => void,
  ): Promise<{ remove: () => Promise<void> }>;
  /** Запрос состояния (Д9): `ESC !?` → 1 байт битового поля, см. `../status.ts`. */
  readStatus(): Promise<{ status: number }>;
}

// Экспортирован: второй потребитель — экран настройки принтера (T-031),
// которому нужны checkPermissions/requestPermissions/listPairedDevices
// напрямую, без обёртки в транспорт.
export const BtClassicSpp = registerPlugin<BtClassicSppPlugin>('BtClassicSpp');

/** Плагин есть только на Android — на вебе/iOS транспорт недоступен. */
export function androidBtPresent(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

/**
 * Веб-слой обновляется по воздуху, а нативная оболочка — только новым APK: пока APK
 * не переустановлен, плагин не знает `openBluetoothSettings`/`requestEnable`, и вызов
 * отвечает отказом. Поэтому обе обёртки возвращают `false`, а не бросают: экран
 * в этом случае просто подсказывает открыть настройки вручную, как и раньше.
 */
export async function openBtSettings(plugin: BtClassicSppPlugin = BtClassicSpp): Promise<boolean> {
  try {
    await plugin.openBluetoothSettings();
    return true;
  } catch {
    return false;
  }
}

/** Попросить включить Bluetooth. `true` — адаптер включён после запроса. */
export async function enableBluetooth(plugin: BtClassicSppPlugin = BtClassicSpp): Promise<boolean> {
  try {
    return (await plugin.requestEnable()).enabled;
  } catch {
    return false;
  }
}

/** Только явное «выключен»: отсутствие поля (старая оболочка) не считается выключенным. */
export function isBluetoothOff(listing: { enabled?: boolean }): boolean {
  return listing.enabled === false;
}

/**
 * Подписка на возврат в приложение. Возвращает отписку. Старая оболочка событие
 * не шлёт — подписка тогда просто ничего не делает, а экран полагается на
 * `visibilitychange` и на кнопку «Обновить».
 */
export function onBtResume(
  callback: () => void,
  plugin: BtClassicSppPlugin = BtClassicSpp,
): () => void {
  let handle: { remove: () => Promise<void> } | null = null;
  let cancelled = false;
  void plugin
    .addListener('resume', callback)
    .then((h) => {
      if (cancelled) void h.remove();
      else handle = h;
    })
    .catch(() => {});
  return () => {
    cancelled = true;
    if (handle) void handle.remove();
  };
}

export type BtClassicSppTransportOptions = {
  /** MAC-адрес спаренного принтера из привязки (T-031). */
  readonly address: string;
  /** Для тестов — реальный код всегда берёт зарегистрированный нативный плагин. */
  readonly plugin?: BtClassicSppPlugin;
};

export function createBtClassicSppTransport(opts: BtClassicSppTransportOptions): PrinterTransport {
  const { address } = opts;
  const plugin = opts.plugin ?? BtClassicSpp;
  let connected = false;

  async function attempt(bytes: Uint8Array): Promise<void> {
    if (!connected) {
      try {
        // Отдельный таймаут на connect, короче всей лестницы плагина: при печати
        // ждать 10 с одной попытки нельзя. Брошенный вызов не висит в плагине —
        // `onAttemptFailed` ниже зовёт `disconnect()`, и тот гасит начатую лестницу.
        // Помечаем `busy`: повару нужно «подождите и повторите», а не «нет связи»
        // (с принтера может печатать другое устройство — повтор его дождётся).
        await withTimeout(plugin.connect({ address }), CONNECT_TIMEOUT_MS);
      } catch (error) {
        if (error instanceof TimeoutError || isUnreachable(error)) {
          throw new PrinterError(PRINTER_BUSY_MESSAGE, error, 'busy');
        }
        throw error;
      }
      connected = true;
    }
    await plugin.write({ data: Array.from(bytes) });
  }

  return {
    id: PRINTER_TRANSPORTS.btClassicSpp,

    send(bytes: Uint8Array): Promise<void> {
      return exclusive(plugin, async () => {
        const permission = await plugin
          .checkPermissions()
          .then((r) => r.bluetooth)
          .catch(() => 'denied' as const);
        if (permission !== 'granted') {
          // Ведёт на настройку принтера, а не показывает системное исключение
          // (критерий 5, T-030): SecurityException повару ничего не говорит.
          throw new PrinterError('Нет разрешения на Bluetooth. Проверьте настройку принтера.');
        }

        // Первое подключение партии ждёт пробуждения принтера — снаружи повторов
        // `sendWithRetry`: у тех потолок 5 с на попытку, а спящий V3BT отвечает
        // через 37–76 с. Повторы ниже — для обрыва уже открытого соединения.
        if (!connected) {
          try {
            await connectAwakeVia(plugin, address);
          } catch (error) {
            throw connectFailure(error, 'Печать не удалась: нет связи с принтером');
          }
          connected = true;
        }
        try {
          await sendWithRetry(
            () => attempt(bytes),
            async () => {
              // Соединение могло протухнуть (экран погас, принтер вне зоны) —
              // следующая попытка подключится заново, а не будет биться в мёртвый сокет.
              connected = false;
              await plugin.disconnect();
            },
          );
        } catch (error) {
          // `busy` (connect завис — держит другое устройство) уходит как есть:
          // повару нужно «подождите и повторите», а не «нет связи с принтером».
          if (error instanceof PrinterError && error.kind === 'busy') {
            throw error;
          }
          throw new PrinterError('Печать не удалась: нет связи с принтером', error);
        }
      });
    },

    disconnect(): Promise<void> {
      // Идемпотентно: вызывается ДАЖЕ если send() ни разу не подключался (пустая
      // партия, печать целиком провалилась на первой этикетке) — плагин сам не
      // ошибается на «нечего закрывать».
      return exclusive(plugin, async () => {
        connected = false;
        await plugin.disconnect();
      });
    },
  };
}

/**
 * Разовый запрос состояния для экрана настройки принтера (Д9) — не транспорт печати.
 *
 * Своё подключение, а не переиспользование `PrinterTransport` из печати: экран
 * настройки не держит открытого адаптера, а поднимать его ради одного запроса
 * не проще, чем подключиться напрямую тем же приёмом, что и `connect()`+`write()`.
 */
/**
 * Подключиться к спаренному принтеру и сразу отпустить его.
 *
 * Отдельная от пробы и статуса операция, потому что владелец 2026-09-24 сформулировал
 * требование именно так: «спарили в системных настройках — нажали „Подсоединиться“ —
 * должен быть только успех». До этого подключение было побочным действием внутри
 * пробной печати, и человек не мог проверить связь, не потратив этикетку.
 *
 * Соединение закрывается сразу: у принтера одно соединение разом, и держать его
 * открытым после проверки значило бы занять слот для всех остальных.
 *
 * Возвращает название сработавшей ступени лестницы, если оболочка его отдаёт
 * (старая не отдаёт — тогда `null`).
 */
export function connectBtPrinter(
  address: string,
  plugin: BtClassicSppPlugin = BtClassicSpp,
): Promise<string | null> {
  return exclusive(plugin, () => connectOnce(address, plugin));
}

async function connectOnce(address: string, plugin: BtClassicSppPlugin): Promise<string | null> {
  const permission = await plugin
    .checkPermissions()
    .then((r) => r.bluetooth)
    .catch(() => 'denied' as const);
  if (permission !== 'granted') {
    throw new PrinterError('Нет разрешения на Bluetooth. Проверьте настройку принтера.');
  }

  try {
    // Каждой попытке — вся лестница плагина (её потолок 10 с), а не четыре секунды:
    // так незащищённые ступени, ради которых она и заведена, доходят до дела. А спящий
    // принтер экран настройки дожидается так же, как печать (`wakeUp.ts`).
    const opened = await connectAwakeVia(plugin, address);
    return opened && typeof opened === 'object' && typeof opened.via === 'string'
      ? opened.via
      : null;
  } catch (error) {
    throw connectFailure(error, 'Не удалось подключиться к принтеру');
  } finally {
    await plugin.disconnect().catch(() => {});
  }
}

export function queryBtPrinterStatus(
  address: string,
  plugin: BtClassicSppPlugin = BtClassicSpp,
): Promise<PrinterStatus> {
  return exclusive(plugin, () => queryStatusOnce(address, plugin));
}

async function queryStatusOnce(
  address: string,
  plugin: BtClassicSppPlugin,
): Promise<PrinterStatus> {
  const permission = await plugin
    .checkPermissions()
    .then((r) => r.bluetooth)
    .catch(() => 'denied' as const);
  if (permission !== 'granted') {
    throw new PrinterError('Нет разрешения на Bluetooth. Проверьте настройку принтера.');
  }

  try {
    // Таймаут ОБЯЗАТЕЛЕН здесь: `connect()` нативного плагина ничем не ограничен
    // (в отличие от `write`/`readStatus`, у которых свой `Future.get` в Java) —
    // если слот SPP принтера занят ДРУГИМ устройством, Android-стек Bluetooth может
    // не отвечать на попытку подключения десятками секунд вместо честного отказа
    // (найдено на живом железе 2026-09-05: два устройства разом). Без гонки со
    // временем кнопка «Проверить принтер» висела бы бесконечно вместо понятной
    // ошибки — то самое «печать не имеет права молча ничего не сделать».
    try {
      await connectAwakeVia(plugin, address);
    } catch (error) {
      throw connectFailure(error, 'Не удалось получить статус принтера');
    }
    const { status } = await withTimeout(plugin.readStatus(), LABEL_TIMEOUT_MS);
    return decodePrinterStatus(status);
  } catch (error) {
    if (error instanceof PrinterError) throw error;
    // Таймаут на этом пути = слот занят другим устройством (см. коммент выше),
    // а не «принтер сломан»: экран настройки скажет «занят, повторите».
    if (error instanceof TimeoutError || isUnreachable(error)) {
      throw new PrinterError(PRINTER_BUSY_MESSAGE, error, 'busy');
    }
    throw new PrinterError('Не удалось получить статус принтера', error);
  } finally {
    // Закрыть ОБЯЗАТЕЛЬНО, а не только на успехе (2026-09-05, живое железо): V3BT
    // держит одно BT Classic соединение разом (спайк T-035, вопрос 6). Разовая
    // проверка статуса с одного устройства держала бы принтер занятым для
    // остальных бесконечно — до следующего перезапуска приложения.
    await plugin.disconnect().catch(() => {});
  }
}

// Классы устройств, которые принтером быть не могут: наушники и колонки, телефоны,
// компьютеры, клавиатуры и мыши, часы, игрушки, медицинские. Дешёвые модули
// принтеров бывают «без категории» — их не отсекаем.
const NOT_A_PRINTER_CLASSES = new Set([0x0100, 0x0200, 0x0400, 0x0500, 0x0700, 0x0800, 0x0900]);

/** Нашли принтеры рядом; `unsupported` — старая оболочка поиска не умеет. */
export type NearbyResult =
  | { readonly kind: 'found'; readonly devices: readonly BtFoundDevice[] }
  | { readonly kind: 'no-permission' }
  | { readonly kind: 'bluetooth-off' }
  | { readonly kind: 'unsupported' };

function hasCode(error: unknown, code: string): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    (error as { code?: unknown }).code === code
  );
}

/**
 * Без имени — не показываем. На живом железе 2026-09-26 поиск рядом с кухней вернул
 * Printer001 и полдюжины безымянных адресов (телефоны и гарнитуры с меняющимися
 * адресами): выглядят как «принтер», выбрать их — гарантированный отказ. Принтер
 * своё имя отдаёт в самом ответе на поиск.
 */
function looksLikePrinter(device: BtFoundDevice): boolean {
  return Boolean(device.name) && !NOT_A_PRINTER_CLASSES.has(device.majorClass);
}

/** Принтеры — первыми, дальше по имени. */
export function sortNearby(devices: readonly BtFoundDevice[]): BtFoundDevice[] {
  return devices
    .filter(looksLikePrinter)
    .slice()
    .sort((a, b) => {
      if (a.printer !== b.printer) return a.printer ? -1 : 1;
      return (a.name ?? '').localeCompare(b.name ?? '', 'ru');
    });
}

/**
 * Найти принтеры рядом — без похода в системные настройки (решение владельца 2026-09-26).
 *
 * Разрешение «Устройства поблизости» на поиск спрашивается здесь, по нажатию, а не
 * при запуске: печати оно не нужно, и у поваров, которым принтер уже настроили,
 * лишнего запроса не будет. `onFound` вызывается по мере нахождения — список
 * растёт на глазах, а не появляется целиком через 12 секунд.
 */
export async function findNearbyPrinters(
  onFound?: (device: BtFoundDevice) => void,
  plugin: BtClassicSppPlugin = BtClassicSpp,
): Promise<NearbyResult> {
  const permissions = await plugin.checkPermissions().catch(() => null);
  if (!permissions || permissions.bluetooth !== 'granted') {
    return { kind: 'no-permission' };
  }
  if (permissions.scan === undefined) {
    return { kind: 'unsupported' };
  }
  if (permissions.scan !== 'granted') {
    const asked = await plugin.requestScanPermission().catch(() => null);
    if (!asked || asked.scan !== 'granted') {
      return { kind: 'no-permission' };
    }
  }

  let subscription: { remove: () => Promise<void> } | null = null;
  if (onFound) {
    subscription = await plugin
      .addListener('deviceFound', (device) => {
        if (looksLikePrinter(device)) onFound(device);
      })
      .catch(() => null);
  }
  try {
    const { devices } = await plugin.discoverDevices();
    return { kind: 'found', devices: sortNearby(devices) };
  } catch (error) {
    if (hasCode(error, 'BLUETOOTH_OFF')) return { kind: 'bluetooth-off' };
    if (hasCode(error, 'NO_SCAN_PERMISSION')) return { kind: 'no-permission' };
    throw new PrinterError('Поиск принтеров не удался', error);
  } finally {
    await subscription?.remove().catch(() => undefined);
  }
}

export async function stopNearbySearch(plugin: BtClassicSppPlugin = BtClassicSpp): Promise<void> {
  await plugin.stopDiscovery().catch(() => undefined);
}

/**
 * Спарить принтер из приложения. PIN V3BT известен (0000) — плагин вводит его сам,
 * без системного окна. Запасной путь: принтеры, которые пускают без сопряжения,
 * до него не доходят (см. `PrinterSetupPage`).
 */
export function pairBtPrinter(
  address: string,
  pin = '0000',
  plugin: BtClassicSppPlugin = BtClassicSpp,
): Promise<void> {
  return exclusive(plugin, async () => {
    try {
      await plugin.pair({ address, pin });
    } catch (error) {
      throw new PrinterError('Спарить принтер не удалось', error);
    }
  });
}
